import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmModal from './ConfirmModal';
import { PAGE_SIZE, Pagination, SortableHeader, SortDirection } from './TableControls';
import trainingService, { PendingReply } from '../services/trainingService';

interface PendingRepliesListProps {
    threads: PendingReply[];
    /** Filters athletes by name. */
    search: string;
    loading: boolean;
    /** Called once a reply is saved, so the parent can drop the thread. */
    onReplied: (entryId: string) => void;
    /** Called once all of an athlete's threads are marked answered. */
    onAthleteAnswered: (userId: string) => void;
}

const DAY_MS = 86_400_000;

type SortField = 'name' | 'conversations' | 'waiting';

/** Older waits are more urgent: red after 3 days, amber after 1. */
const urgencyClass = (days: number) =>
    days >= 3 ? 'bg-red-100 text-red-700'
        : days >= 1 ? 'bg-amber-100 text-amber-700'
            : 'bg-slate-100 text-slate-500';

/**
 * Comment threads still waiting on a trainer reply, grouped by athlete and
 * ordered by who has waited longest. Replying removes a thread from the list.
 */
const PendingRepliesList: React.FC<PendingRepliesListProps> = ({ threads, search, loading, onReplied, onAthleteAnswered }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'it' ? 'it-IT' : 'en-US';
    // Accordion: one athlete open at a time, all closed by default.
    const [openUserId, setOpenUserId] = useState<string | null>(null);
    const [markingUserId, setMarkingUserId] = useState<string | null>(null);
    const [markFailedUserId, setMarkFailedUserId] = useState<string | null>(null);
    const [confirmTarget, setConfirmTarget] = useState<{ userId: string; athleteName: string; count: number } | null>(null);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [sending, setSending] = useState<Record<string, boolean>>({});
    const [failed, setFailed] = useState<Record<string, boolean>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    // Cycles asc -> desc -> unsorted (back to longest-waiting first), like the athletes table.
    const handleSort = (field: SortField) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDirection('asc');
        } else if (sortDirection === 'asc') {
            setSortDirection('desc');
        } else {
            setSortField(null);
        }
        setCurrentPage(1);
    };

    const markAllAnswered = async (userId: string) => {
        setMarkingUserId(userId);
        setMarkFailedUserId(null);
        try {
            await trainingService.markAthleteAnswered(userId);
            setOpenUserId(null);
            onAthleteAnswered(userId);
        } catch {
            setMarkFailedUserId(userId);
        } finally {
            setConfirmTarget(null);
            setMarkingUserId(null);
        }
    };

    const sendReply = async (entryId: string) => {
        const msg = (drafts[entryId] || '').trim();
        if (!msg || sending[entryId]) return;
        setSending((prev) => ({ ...prev, [entryId]: true }));
        setFailed((prev) => ({ ...prev, [entryId]: false }));
        try {
            await trainingService.addComment(entryId, msg);
            setDrafts((prev) => ({ ...prev, [entryId]: '' }));
            onReplied(entryId);
        } catch {
            // Keep the draft so nothing typed is lost.
            setFailed((prev) => ({ ...prev, [entryId]: true }));
        } finally {
            setSending((prev) => ({ ...prev, [entryId]: false }));
        }
    };

    // Threads arrive oldest first, so the first one seen per athlete is their
    // longest wait and the groups come out longest-waiting athlete first.
    const groups = useMemo(() => {
        const byUser = new Map<string, { userId: string; athleteName: string; threads: PendingReply[] }>();
        for (const thread of threads) {
            const group = byUser.get(thread.userId)
                ?? { userId: thread.userId, athleteName: thread.athleteName, threads: [] };
            group.threads.push(thread);
            byUser.set(thread.userId, group);
        }
        const term = search.trim().toLowerCase();
        const list = Array.from(byUser.values()).filter((g) => g.athleteName.toLowerCase().includes(term));
        if (!sortField) return list;
        const value = (g: typeof list[number]): string | number =>
            sortField === 'name' ? g.athleteName.toLowerCase()
                : sortField === 'conversations' ? g.threads.length
                    // Oldest wait = smallest timestamp; asc shows the longest wait first.
                    : new Date(g.threads[0].waitingSince).getTime();
        return [...list].sort((a, b) => {
            const av = value(a);
            const bv = value(b);
            const result = typeof av === 'string' ? av.localeCompare(bv as string) : av - (bv as number);
            return sortDirection === 'asc' ? result : -result;
        });
    }, [threads, search, sortField, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
    // Clamp: replying to the last athlete on the final page empties it.
    const safePage = Math.min(currentPage, totalPages);
    const pageGroups = groups.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const daysWaiting = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS));

    const waitLabel = (iso: string) => {
        const days = daysWaiting(iso);
        return days === 0 ? t('pendingReplies.today') : t('pendingReplies.daysAgo', { count: days });
    };

    const formatDay = (date: string) =>
        new Date(`${date}T00:00:00`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });

    const openThread = (thread: PendingReply) =>
        navigate(`/users/${thread.userId}/training`, {
            state: { openEntryId: thread.entryId, weekDate: thread.trainingDate },
        });

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-400">
                <i className="fa-solid fa-spinner fa-spin text-3xl"></i>
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="py-16 px-6 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mb-4">
                    <i className="fa-solid fa-check-double text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-1">{t('pendingReplies.emptyHeading')}</h3>
                <p className="text-sm text-slate-500">{t('pendingReplies.emptyDescription')}</p>
            </div>
        );
    }

    return (
        <div>
            {confirmTarget && (
                <ConfirmModal
                    icon="fa-check-double"
                    title={t('pendingReplies.markAllAnswered')}
                    message={t('pendingReplies.markAllConfirm', { count: confirmTarget.count, name: confirmTarget.athleteName })}
                    confirmLabel={t('pendingReplies.markAllAnswered')}
                    busy={markingUserId === confirmTarget.userId}
                    onConfirm={() => markAllAnswered(confirmTarget.userId)}
                    onCancel={() => setConfirmTarget(null)}
                />
            )}
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 border-b border-blue-600 text-xs font-black text-white uppercase tracking-wider rounded-t-xl">
                <div className="col-span-1">{t('users.table.sno')}</div>
                <SortableHeader label={t('pendingReplies.colAthlete')} className="col-span-4" active={sortField === 'name'} direction={sortDirection} onSort={() => handleSort('name')} />
                <SortableHeader label={t('pendingReplies.colConversations')} className="col-span-3" active={sortField === 'conversations'} direction={sortDirection} onSort={() => handleSort('conversations')} />
                <SortableHeader label={t('pendingReplies.colWaiting')} className="col-span-3" active={sortField === 'waiting'} direction={sortDirection} onSort={() => handleSort('waiting')} />
                <div className="col-span-1">{t('common.actions')}</div>
            </div>
            <div className="divide-y divide-slate-100">
            {pageGroups.map((group, index) => {
                const sno = ((safePage - 1) * PAGE_SIZE + index + 1).toString().padStart(3, '0');
                const isOpen = openUserId === group.userId;
                const oldestDays = daysWaiting(group.threads[0].waitingSince);
                return (
                    <div key={group.userId}>
                        {/* Athlete row, laid out like the athletes table: a card below md, the grid above. */}
                        <button
                            onClick={() => setOpenUserId(isOpen ? null : group.userId)}
                            aria-expanded={isOpen}
                            className={`w-full text-left transition-colors ${isOpen ? 'bg-cyan-50/60' : 'hover:bg-slate-50 active:bg-slate-50'}`}
                        >
                            <div className="md:hidden px-3 py-2.5 flex items-center gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[11px] font-semibold text-slate-300 flex-shrink-0 tabular-nums">{sno}</span>
                                        <span className="text-sm font-semibold text-slate-800 truncate">{group.athleteName}</span>
                                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 animate-pulse" />
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-1.5">
                                        <span className="text-xs text-slate-400">
                                            {t('pendingReplies.conversations', { count: group.threads.length })}
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${urgencyClass(oldestDays)}`}>
                                            <i className="fa-regular fa-clock text-[9px]"></i>
                                            {waitLabel(group.threads[0].waitingSince)}
                                        </span>
                                    </div>
                                </div>
                                <span className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                                    <i className={`fa-solid fa-chevron-down text-slate-500 text-sm transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
                                </span>
                            </div>

                            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
                                <div className="col-span-1 font-semibold text-slate-700">{sno}</div>
                                <div className="col-span-4 font-medium text-slate-700 flex items-center gap-2 min-w-0">
                                    <span className="truncate">{group.athleteName}</span>
                                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm animate-pulse flex-shrink-0" />
                                </div>
                                <div className="col-span-3 text-slate-700">{group.threads.length}</div>
                                <div className="col-span-3">
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${urgencyClass(oldestDays)}`}>
                                        <i className="fa-regular fa-clock text-[10px]"></i>
                                        {waitLabel(group.threads[0].waitingSince)}
                                    </span>
                                </div>
                                <div className="col-span-1">
                                    <span className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-lg transition-colors">
                                        <i className={`fa-solid fa-chevron-down text-slate-600 text-sm transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
                                    </span>
                                </div>
                            </div>
                        </button>

                        {/* Threads */}
                        {isOpen && (
                            <div className="pb-3 px-4 md:px-6 space-y-2 bg-cyan-50/30">
                                {/* Close everything for this athlete without replying */}
                                <div className="flex items-center justify-between gap-3 pt-1">
                                    <p className="text-xs text-slate-500">{t('pendingReplies.markAllHint')}</p>
                                    <button
                                        onClick={() => setConfirmTarget({ userId: group.userId, athleteName: group.athleteName, count: group.threads.length })}
                                        disabled={markingUserId === group.userId}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 text-xs font-bold transition-colors flex-shrink-0 disabled:opacity-50"
                                    >
                                        <i className={`fa-solid ${markingUserId === group.userId ? 'fa-spinner fa-spin' : 'fa-check-double'}`}></i>
                                        {t('pendingReplies.markAllAnswered')}
                                    </button>
                                </div>
                                {markFailedUserId === group.userId && (
                                    <p className="text-xs text-red-600">{t('pendingReplies.markAllFailed')}</p>
                                )}
                                {group.threads.map((thread) => (
                                    <div
                                        key={thread.entryId}
                                        className="p-3 rounded-lg border border-slate-200 bg-slate-50/60"
                                    >
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                            <span className="font-semibold text-slate-700">
                                                <i className="fa-regular fa-calendar mr-1 text-slate-400"></i>
                                                {formatDay(thread.trainingDate)}
                                            </span>
                                            <span className="text-slate-400">·</span>
                                            <span className="text-slate-500 truncate">
                                                {thread.entryTitle || t('pendingReplies.noSession')}
                                            </span>
                                            <span className={`ml-auto ${daysWaiting(thread.waitingSince) >= 3 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                                                {t('pendingReplies.waiting', { time: waitLabel(thread.waitingSince) })}
                                            </span>
                                        </div>

                                        {/* Every unanswered message, so the trainer has the full question */}
                                        <div className="mt-2 space-y-1.5">
                                            {thread.messages.map((m) => (
                                                <div key={m.id} className="max-w-[90%]">
                                                    <p className="text-sm text-slate-700 bg-white border border-slate-200 rounded-lg rounded-tl-none px-3 py-2 whitespace-pre-wrap break-words">
                                                        {m.message}
                                                    </p>
                                                    <p className="text-[11px] text-slate-400 mt-0.5 ml-1">
                                                        {new Date(m.createdAt).toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Inline reply */}
                                        <div className="mt-2 flex items-center gap-2">
                                            {/* text-base below md: anything smaller makes iOS Safari zoom in on focus. */}
                                            <input
                                                type="text"
                                                value={drafts[thread.entryId] || ''}
                                                onChange={(e) => setDrafts((prev) => ({ ...prev, [thread.entryId]: e.target.value }))}
                                                onKeyDown={(e) => { if (e.key === 'Enter') sendReply(thread.entryId); }}
                                                disabled={sending[thread.entryId]}
                                                placeholder={t('pendingReplies.replyPlaceholder', { name: group.athleteName.split(' ')[0] })}
                                                aria-label={t('pendingReplies.replyPlaceholder', { name: group.athleteName.split(' ')[0] })}
                                                className="flex-1 min-w-0 text-base md:text-sm rounded-lg border border-slate-200 px-3 py-2 bg-white placeholder-slate-400 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-100 disabled:opacity-60"
                                            />
                                            <button
                                                onClick={() => sendReply(thread.entryId)}
                                                disabled={sending[thread.entryId] || !(drafts[thread.entryId] || '').trim()}
                                                className="flex items-center justify-center gap-2 px-3 md:px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg text-sm font-bold shadow-sm transition-all flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <i className={`fa-solid ${sending[thread.entryId] ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                                                <span className="hidden sm:inline">{t('pendingReplies.send')}</span>
                                            </button>
                                            <button
                                                onClick={() => openThread(thread)}
                                                title={t('pendingReplies.openPlan')}
                                                aria-label={t('pendingReplies.openPlan')}
                                                className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-cyan-600 hover:border-cyan-300 transition-colors flex-shrink-0"
                                            >
                                                <i className="fa-solid fa-arrow-up-right-from-square text-sm"></i>
                                            </button>
                                        </div>
                                        {failed[thread.entryId] && (
                                            <p className="mt-1 text-xs text-red-600">{t('pendingReplies.sendFailed')}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
            </div>
            <Pagination
                page={safePage}
                totalPages={totalPages}
                shown={pageGroups.length}
                total={groups.length}
                onPage={setCurrentPage}
            />
        </div>
    );
};

export default PendingRepliesList;
